import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Shield, Car, Video, DoorOpen, CalendarCheck, ShoppingBag, HardHat,
  KeyRound, UserCheck, Building2, Phone, MapPin, ChevronRight, ShieldCheck,
  Radio, FileCheck, Clock, Quote, CalendarDays, Dog, Warehouse, Hotel, Building, School, HeartPulse,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { MaskLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Layout";
import { SectionHead, FaqBlock, CtaBand } from "@/components/Blocks";
import { CITIES, SERVICES, IMAGES, INSIGHTS, TESTIMONIALS, PHONE, PHONE_HREF, faqLd, SITE_URL, EMAIL } from "@/data/contentEngine";

const SERVICE_ICONS = {
  "manned-guarding": Shield,
  "mobile-patrols": Car,
  "cctv-monitoring": Video,
  "door-supervision": DoorOpen,
  "event-security": CalendarCheck,
  "retail-security": ShoppingBag,
  "construction-site-security": HardHat,
  "key-holding-alarm-response": KeyRound,
  "close-protection": UserCheck,
  "corporate-security": Building2,
  "k9-security-patrols": Dog,
  "warehouse-industrial-security": Warehouse,
  "hotel-hospitality-security": Hotel,
  "vacant-property-security": Building,
  "school-campus-security": School,
  "hospital-healthcare-security": HeartPulse,
};

const HOME_FAQS = [
  {
    q: "Which parts of Northern Ireland do you cover?",
    a: "Every city and major town — Belfast, Derry / Londonderry, Lisburn, Newry, Armagh, Bangor, Craigavon, Newtownabbey, Ballymena, Coleraine, Omagh, Enniskillen, Antrim, Larne, Strabane, Dungannon and Portadown, plus surrounding rural districts.",
  },
  {
    q: "Are your security officers licensed and vetted?",
    a: "Yes. Every frontline officer holds a valid Security Industry Authority (SIA) licence and is vetted to BS7858 standard. Licence details are supplied with every contract.",
  },
  {
    q: "Can you deploy security at short notice?",
    a: "Yes. Our Northern Ireland control room maintains an on-call roster of vetted officers, so emergency cover for incidents, staff absence or sudden risk can often begin the same day.",
  },
  {
    q: "Do you provide reports of guard and patrol activity?",
    a: "Every assignment includes electronic patrol verification, GPS-stamped visit logs and incident reporting, giving you a timestamped, auditable record of your protection.",
  },
  {
    q: "How do I get a quotation?",
    a: `Call ${PHONE} or email ${EMAIL}. We will arrange a free, no-obligation risk assessment of your premises and provide a tailored quotation.`,
  },
];

const STATS = [
  { value: "43", label: "Cities & Towns Covered" },
  { value: "24/7", label: "Control Room & Response" },
  { value: "16", label: "Specialist Security Services" },
  { value: "100%", label: "Northern Ireland Coverage" },
];

const WHY = [
  { icon: ShieldCheck, title: "SIA Licensed Officers", text: "Every frontline officer holds a valid Security Industry Authority licence for their role — guarding, door supervision or CCTV operation." },
  { icon: FileCheck, title: "BS7858 Vetting", text: "All staff are screened to the BS7858 security vetting standard, with employment history, identity and right-to-work fully verified." },
  { icon: Radio, title: "24/7 Control Room", text: "A Northern Ireland control room supports every shift — welfare checks, alarm escalation and rapid backup whenever an officer calls it in." },
  { icon: Clock, title: "Verified Reporting", text: "Electronic patrol verification, GPS-stamped visits and digital incident reports give you an auditable record of every shift." },
];

const PROCESS = [
  { step: "01", title: "Free Risk Assessment", text: "We walk your site, identify vulnerabilities and agree exactly what protection you need — no obligation, no jargon." },
  { step: "02", title: "Tailored Deployment Plan", text: "Assignment instructions, patrol routes, escalation contacts and reporting are written for your premises before anyone starts." },
  { step: "03", title: "Licensed Officers On Site", text: "SIA-licensed, BS7858-vetted officers deploy to your site — uniformed, briefed and connected to our 24/7 control room." },
  { step: "04", title: "Verified Proof, Every Shift", text: "Digital patrol logs, incident reports and GPS verification land in your inbox — protection you can actually audit." },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.25]);
  return (
    <section className="grain-overlay relative flex min-h-[92vh] items-center overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img
          src={IMAGES.guard}
          alt="SIA-licensed Xtreme Protection security officer on duty in Northern Ireland"
          className="h-[120%] w-full object-cover"
        />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(5,7,10,0.5) 0%, rgba(5,7,10,0.82) 60%, #05070A 100%)",
        }}
      />
      <motion.div className="relative mx-auto w-full max-w-7xl px-4 py-28 sm:px-6 lg:px-12" style={{ opacity }}>
        <MaskLine>
          <span className="inline-flex items-center gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-emerald-400" />
            SIA Licensed · Northern Ireland Wide
          </span>
        </MaskLine>
        <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl" data-testid="hero-title">
          <MaskLine delay={0.15}>Elite Security Guard</MaskLine>
          <MaskLine delay={0.3}>Services Across</MaskLine>
          <MaskLine delay={0.45}>
            <span className="text-amber-400">Northern Ireland</span>
          </MaskLine>
        </h1>
        <MaskLine delay={0.65}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="hero-subtitle">
            Xtreme Protection deploys SIA-licensed security guards, mobile patrols, CCTV
            monitoring and rapid alarm response teams across every city and town in Northern
            Ireland — engineered deterrence with verified proof on every shift.
          </p>
        </MaskLine>
        <MaskLine delay={0.85}>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={PHONE_HREF}
              data-testid="hero-call-button"
              className="flex items-center justify-center gap-2 rounded-sm bg-amber-500 px-8 py-4 font-display text-base font-bold uppercase tracking-[0.15em] text-obsidian-950 transition-all duration-300 hover:bg-amber-400 active:scale-95"
            >
              <Phone className="h-4 w-4" /> Call {PHONE}
            </a>
            <Link
              to="/services"
              data-testid="hero-services-button"
              className="flex items-center justify-center gap-2 rounded-sm border border-slate-600 px-8 py-4 font-display text-base font-bold uppercase tracking-[0.15em] text-slate-200 transition-all duration-300 hover:border-amber-500 hover:text-amber-400 active:scale-95"
            >
              Explore Services <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </MaskLine>
        <MaskLine delay={1}>
          <div className="mt-14 grid grid-cols-2 gap-6 border-t border-slate-700/60 pt-8 sm:grid-cols-4" data-testid="hero-stats">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-black text-amber-400 sm:text-4xl">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:text-xs">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </MaskLine>
      </motion.div>
    </section>
  );
};

const Home = () => (
  <>
    <Seo
      title="Security Guards Northern Ireland | SIA Security Services | Xtreme Protection"
      description="SIA-licensed security guards, mobile patrols, CCTV monitoring, door supervision & alarm response across Belfast, Derry/Londonderry & all Northern Ireland. Call +44 2046 402840."
      path="/"
      jsonLd={faqLd(HOME_FAQS)}
    />
    <Hero />
    <Marquee />

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="services-section">
      <SectionHead
        eyebrow="Security Services Northern Ireland"
        title="Every discipline. One accountable partner."
        sub="From static guarding to close protection, ten specialist security services delivered by licensed officers across all of Northern Ireland."
        testId="services-heading"
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.slug];
          return (
            <Reveal key={s.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="tactical-card group flex h-full flex-col rounded-md p-6"
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-7 w-7 text-amber-500" />
                  <ChevronRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                  {s.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.short}</p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500/80">
                  Available across 43 NI locations
                </p>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>

    <section className="border-y border-slate-800 bg-obsidian-900" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <SectionHead
              eyebrow="Why Xtreme Protection"
              title="Compliance you can verify. Protection you can audit."
              sub="We are a Northern Ireland security company built on licensing, vetting and proof — not promises. Every officer is accountable, every shift is recorded."
              testId="why-heading"
            />
            <div className="mt-10 space-y-6">
              {WHY.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.08}>
                  <div className="flex gap-4" data-testid={`why-item-${i}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-amber-500/40 bg-amber-500/10">
                      <w.icon className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-100">
                        {w.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="relative h-full min-h-[420px] overflow-hidden rounded-md border border-slate-800">
              <img
                src={IMAGES.officer}
                alt="Close protection security officer providing professional guarding services in Northern Ireland"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(200deg, rgba(5,7,10,0.1) 0%, rgba(5,7,10,0.85) 100%)" }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">
                  On duty · Northern Ireland
                </p>
                <p className="mt-2 font-display text-2xl font-bold uppercase text-slate-100">
                  Engineered deterrence, verified every shift
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="coverage-section">
      <SectionHead
        eyebrow="Areas We Cover"
        title="Security guards in every Northern Ireland city"
        sub="Locally recruited officers in 43 cities, towns and villages — click any location for dedicated local coverage and services."
        testId="coverage-heading"
      />
      <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {CITIES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 6) * 0.05}>
            <Link
              to={`/cities/${c.slug}`}
              data-testid={`city-card-${c.slug}`}
              className="tactical-card group flex h-full flex-col rounded-md p-4"
            >
              <MapPin className="h-5 w-5 text-amber-500" />
              <h3 className="mt-3 font-display text-base font-bold uppercase leading-tight tracking-wide text-slate-100">
                {c.name}
              </h3>
              <p className="mt-1 text-[11px] text-slate-500">{c.county}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-y border-slate-800 bg-obsidian-900" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
        <SectionHead
          eyebrow="How It Works"
          title="From first call to officers on site"
          testId="process-heading"
        />
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.1}>
              <div className="tactical-card h-full rounded-md p-6" data-testid={`process-step-${i}`}>
                <p className="font-display text-4xl font-black text-amber-500/30">{p.step}</p>
                <h3 className="mt-3 font-display text-lg font-bold uppercase tracking-wide text-slate-100">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="testimonials-section">
      <SectionHead
        eyebrow="Client Voices"
        title="Trusted on sites across Northern Ireland"
        sub="What facilities managers, organisers and business owners say about working with our officers."
        testId="testimonials-heading"
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <figure className="tactical-card flex h-full flex-col rounded-md p-6" data-testid={`testimonial-${i}`}>
              <Quote className="h-6 w-6 text-amber-500/60" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">
                {t.quote}
              </blockquote>
              <figcaption className="mt-5 border-t border-slate-800 pt-4">
                <p className="font-display text-sm font-bold uppercase tracking-wide text-slate-100">
                  {t.role}
                </p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {t.sector} · {t.location}
                </p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-y border-slate-800 bg-obsidian-900" data-testid="insights-section">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            eyebrow="Security Insights"
            title="Advice from the control room"
            sub="Practical Northern Ireland security guidance — licensing, loss prevention, site protection and event safety."
            testId="insights-heading"
          />
          <Link
            to="/insights"
            data-testid="insights-view-all"
            className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-amber-400 transition-colors hover:text-amber-300"
          >
            All Articles <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {INSIGHTS.slice(0, 3).map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`home-insight-${a.slug}`}
                className="tactical-card group flex h-full flex-col rounded-md p-6"
              >
                <span className="w-fit rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">
                  {a.category}
                </span>
                <h3 className="mt-4 flex-1 font-display text-lg font-bold uppercase leading-snug tracking-wide text-slate-100">
                  {a.title}
                </h3>
                <p className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <CalendarDays className="h-3 w-3" />
                  {new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  <span className="ml-2">{a.readTime}</span>
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="faq-section">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHead
            eyebrow="Questions, Answered"
            title="Security services in Northern Ireland — FAQs"
            sub="Straight answers about licensing, coverage, deployment and reporting. Anything else, call the team directly."
            testId="faq-heading"
          />
        </div>
        <div className="lg:col-span-3">
          <Reveal>
            <FaqBlock faqs={HOME_FAQS} testId="home-faq" />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
      <CtaBand
        title="Talk to a Northern Ireland security specialist"
        sub="Free risk assessment, transparent quotation and same-day deployment available. No call centres — you speak directly to our control room."
      />
    </section>
  </>
);

export default Home;
